/* eslint-disable react/prop-types */
import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import * as Icons from '@mui/icons-material';
import {
  PATHS_WITH_QUERY_PARAMS,
  REMOVE_FROM_URLSEARCH
} from '../../constants/sidebar';
import { getSidebarItems } from '../../constants/routes';
import { findOpenPaths } from '../../utils';
import { ROLES, resolveUserRole } from '../../constants/rolePermissions';
import { sendAnalyticsSessionLog } from '../../utils/analytics';
import { sendRequest } from '../../utils/dateHelpers';
import {
  resolveIntegrationCompleted,
  resolvePaymentCompleted,
  resolvePaymentCompletedFromResponse,
  resolvePlanCompleted,
  resolvePlanCompletedFromResponse
} from '../../utils/setupProgress';
import {
  hasNoCustomProjects,
  hasNoCustomTags
} from '../../utils/managementNotifications';

const HISTORICAL_ANALYSIS_PATH = '/settings/historical-analysis';
const DEVELOPERS_SETTINGS_PATH = '/settings/developers';
const PROJECTS_SETTINGS_PATH = '/settings/project-management';
const TAGS_SETTINGS_PATH = '/settings/tags';
const DEV_FEED_PATH = '/dev-feed';
const BILLING_PROFILE_URL = '/billing/profile';
const DEVELOPER_MERGE_SUGGESTIONS_STATUS_URL =
  '/developers/merge-suggestions/status';
const PROJECT_SUGGESTIONS_STATUS_URL = '/project-management/suggestions/status';
const PROJECT_MANAGEMENT_LIST_URL = '/project-management/list';
const TAGS_LIST_URL = '/tags/get';
const DEV_FEED_NOTIFICATION_URL = '/dev-feed/notifications';
const DEV_FEED_MARK_READ_URL = '/dev-feed/notifications/mark-read';
const HISTORICAL_ANALYSIS_NOTIFICATION_MONTHS = 3;
const BOTTOM_DOCK_SECTION_LABELS = ['Management', 'Account Settings'];

const parseDateValue = value => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();

    if (!trimmedValue) return null;

    const normalizedDate = /^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)
      ? new Date(`${trimmedValue}T00:00:00`)
      : new Date(trimmedValue);

    return Number.isNaN(normalizedDate.getTime()) ? null : normalizedDate;
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const shouldShowHistoricalAnalysisNotification = analysisStartDate => {
  const parsedStartDate = parseDateValue(analysisStartDate);

  if (!parsedStartDate) return false;

  const cutoffDate = new Date();
  cutoffDate.setMonth(
    cutoffDate.getMonth() - HISTORICAL_ANALYSIS_NOTIFICATION_MONTHS
  );

  return parsedStartDate > cutoffDate;
};

// eslint-disable-next-line no-unused-vars
const hasItemNotification = item =>
  Boolean(item?.notification) ||
  Number(item?.notificationCount || 0) > 0 ||
  Boolean(item?.nodes?.some(child => hasItemNotification(child)));

// eslint-disable-next-line no-unused-vars
const getItemNotificationCount = item => {
  const ownCount = Number(item?.notificationCount || 0);

  if (!item?.nodes?.length) return ownCount;

  return item.nodes.reduce(
    (total, child) => total + getItemNotificationCount(child),
    ownCount
  );
};

// eslint-disable-next-line no-unused-vars
const NotificationIndicator = ({ className = '' }) => (
  <span
    aria-hidden="true"
    className={`relative inline-flex h-2.5 w-2.5 shrink-0 ${className}`}
  >
    <span className="absolute inset-0 rounded-full bg-rose-500 opacity-75 animate-ping" />
    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-600 shadow-[0_0_0_4px_rgba(225,29,72,0.18)]" />
  </span>
);

// eslint-disable-next-line no-unused-vars
const NotificationCountBadge = ({ count = 0, className = '' }) => {
  const normalizedCount = Number(count || 0);
  if (normalizedCount <= 0) return null;

  return (
    <span
      className={`inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-rose-600 px-1.5 text-[11px] font-bold leading-none text-white shadow-[0_0_0_3px_rgba(225,29,72,0.16)] ${className}`}
    >
      {normalizedCount > 99 ? '99+' : normalizedCount}
    </span>
  );
};

const handleLinkClick = (path, navigate) => {
  sendAnalyticsSessionLog({
    eventName: 'click',
    payload: {
      cta_id: `sidebar_nav_${path?.replace(/\//g, '_') || 'unknown'}`,
      path: window.location.pathname,
      target_path: path
    }
  });

  const queryParams = new URLSearchParams(window.location.search);
  const pathHasQueryParams = PATHS_WITH_QUERY_PARAMS.includes(path);
  const keysToRemove =
    REMOVE_FROM_URLSEARCH?.[path] || REMOVE_FROM_URLSEARCH?.['*'];

  keysToRemove.forEach(key => {
    const keyRegex = new RegExp(`^${key}(\\[\\d*\\]|\\[\\])?$`);
    for (const existingKey of Array.from(queryParams.keys())) {
      if (keyRegex.test(existingKey)) {
        queryParams.delete(existingKey);
      }
    }
  });

  const queryString =
    pathHasQueryParams && queryParams.toString() !== ''
      ? '?' + queryParams.toString()
      : '';

  navigate(`${path}${queryString}`);
  setTimeout(() => window.scrollTo(0, 0), 10);
};

const MenuItem = ({
  item,
  initiallyOpenPaths = [],
  depth = 0,
  searchQuery = ''
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const menuItemRef = useRef(null);
  const [isOpen, setIsOpen] = useState(initiallyOpenPaths.includes(item.label));

  // Auto-open if search matches children
  useEffect(() => {
    if (searchQuery && item.nodes) {
      const hasMatchingChild = item.nodes.some(child =>
        child.label.toLowerCase().includes(searchQuery.toLowerCase())
      );
      if (hasMatchingChild) {
        setIsOpen(true);
      }
    }
  }, [searchQuery, item.nodes]);

  // Scroll to active item
  useEffect(() => {
    const isActive = item.path === location.pathname;
    if (isActive && menuItemRef.current) {
      setTimeout(() => {
        menuItemRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  }, [location.pathname, item.path]);

  const hasChildren = item.nodes && item.nodes.length > 0;
  const IconComponent = item.icon && Icons[item.icon] ? Icons[item.icon] : null;
  const notificationCount = getItemNotificationCount(item);
  const hasNotification = hasItemNotification(item);

  const isActive = item.path === location.pathname;
  const isActiveParent = initiallyOpenPaths.includes(item.label);

  const handleClick = () => {
    if (hasChildren) {
      const willOpen = !isOpen;
      setIsOpen(willOpen);

      // Scroll to show the expanded submenu
      if (willOpen && menuItemRef.current) {
        setTimeout(() => {
          menuItemRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
          });
        }, 100);
      }
    } else if (item.path) {
      handleLinkClick(item.path, navigate);
    }
  };

  const isChild = depth > 0;
  const shouldShowNotification = !hasChildren || !isOpen;

  return (
    <div ref={menuItemRef} className={isChild ? 'mb-0.5' : 'mb-1'}>
      <button
        onClick={handleClick}
        className={`group relative flex w-full items-center gap-2.5 rounded-lg text-left transition-all duration-200 ease-out ${
          isChild ? 'px-2.5 py-1.5' : 'px-2.5 py-2'
        } ${
          isActive
            ? 'bg-primary/15 text-white shadow-sm'
            : isActiveParent
              ? 'text-white'
              : 'text-bodydark2 hover:scale-[1.02] hover:bg-white/8 hover:text-white hover:shadow-sm'
        }`}
      >
        {/* Active indicator - more prominent */}
        {isActive && !isChild && (
          <div className="absolute left-0 top-1/2 h-10 w-1 -translate-y-1/2 rounded-r-full bg-primary shadow-lg shadow-primary/50" />
        )}

        {IconComponent && (
          <div
            className={`flex shrink-0 items-center justify-center rounded-lg transition-all duration-200 ${
              isChild ? 'h-6 w-6' : 'h-8 w-8'
            } ${
              isActive
                ? 'bg-primary text-white shadow-lg shadow-primary/30'
                : 'bg-white/5 text-bodydark2 group-hover:scale-110 group-hover:bg-white/10 group-hover:text-white'
            }`}
          >
            <IconComponent fontSize="small" />
          </div>
        )}

        <span
          className={`flex-1 truncate font-medium transition-all ${
            isChild ? 'text-[13px]' : 'text-sm'
          } ${isActive ? 'font-semibold' : ''}`}
        >
          {item.label}
        </span>

        {item.badge && (
          <span className="rounded-full bg-amber-500/20 px-2.5 py-1 text-xs font-semibold text-amber-400 ring-1 ring-amber-500/30 transition-all group-hover:ring-amber-500/50">
            {item.badge}
          </span>
        )}

        {shouldShowNotification && notificationCount > 0 && (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white shadow-lg shadow-rose-500/50">
            {notificationCount > 99 ? '99+' : notificationCount}
          </span>
        )}

        {shouldShowNotification && !notificationCount && hasNotification && (
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-500 shadow-lg shadow-rose-500/50" />
          </span>
        )}

        {hasChildren && (
          <Icons.ExpandMore
            fontSize="small"
            className={`shrink-0 text-bodydark2 transition-all duration-300 ease-out group-hover:text-white ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        )}
      </button>

      {hasChildren && (
        <div
          className={`ml-9 overflow-hidden border-l-2 border-white/5 pl-3 transition-all duration-300 ease-out ${
            isOpen ? 'mt-1 max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="space-y-0.5 py-0.5">
            {item.nodes.map((child, idx) => (
              <MenuItem
                key={idx}
                item={child}
                initiallyOpenPaths={initiallyOpenPaths}
                depth={depth + 1}
                searchQuery={searchQuery}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SidebarContent = ({ isCollapsed = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userInfo, user } = useSelector(state => state);
  const effectiveUser = userInfo?.user || user || {};
  const [
    showHistoricalAnalysisNotification,
    setShowHistoricalAnalysisNotification
  ] = useState(false);
  const [
    showDeveloperMergeSuggestionNotification,
    setShowDeveloperMergeSuggestionNotification
  ] = useState(false);
  const [showProjectsNotification, setShowProjectsNotification] =
    useState(false);
  const [
    showProjectSuggestionNotification,
    setShowProjectSuggestionNotification
  ] = useState(false);
  const [showTagsNotification, setShowTagsNotification] = useState(false);
  const [devFeedNotificationCount, setDevFeedNotificationCount] = useState(0);

  const userRole = resolveUserRole(
    effectiveUser?.role,
    effectiveUser?.roleName,
    effectiveUser?.userRole,
    userInfo?.role,
    user?.role,
    user?.roleName,
    user?.userRole
  );

  useEffect(() => {
    let isActive = true;

    const fetchNotificationState = async () => {
      try {
        const canSeeSetupNotifications = [
          ROLES.ACCOUNT_ADMIN,
          ROLES.PANEL_ADMIN
        ].includes(userRole);
        const canSeeHistoricalAnalysis = [ROLES.ACCOUNT_ADMIN].includes(
          userRole
        );
        const canSeeDeveloperSuggestions = [
          ROLES.ACCOUNT_ADMIN,
          ROLES.PANEL_ADMIN
        ].includes(userRole);
        const canSeeProjectSuggestions = canSeeSetupNotifications;
        const canSeeDevFeed = [
          ROLES.ACCOUNT_ADMIN,
          ROLES.PANEL_ADMIN,
          ROLES.VIEWER
        ].includes(userRole);
        const isDevFeedPage = location.pathname === DEV_FEED_PATH;

        const [
          billingProfileResult,
          developerSuggestionResult,
          projectSuggestionResult,
          devFeedNotificationResult
        ] = await Promise.allSettled([
          canSeeHistoricalAnalysis
            ? sendRequest(BILLING_PROFILE_URL, 'GET')
            : Promise.resolve(null),
          canSeeDeveloperSuggestions
            ? sendRequest(DEVELOPER_MERGE_SUGGESTIONS_STATUS_URL, 'GET')
            : Promise.resolve(null),
          canSeeProjectSuggestions
            ? sendRequest(PROJECT_SUGGESTIONS_STATUS_URL, 'GET')
            : Promise.resolve(null),
          canSeeDevFeed
            ? sendRequest(
                isDevFeedPage
                  ? DEV_FEED_MARK_READ_URL
                  : DEV_FEED_NOTIFICATION_URL,
                isDevFeedPage ? 'POST' : 'GET'
              )
            : Promise.resolve(null)
        ]);
        const [projectListResult, tagsListResult] = await Promise.allSettled([
          canSeeSetupNotifications
            ? sendRequest(PROJECT_MANAGEMENT_LIST_URL, 'get')
            : Promise.resolve(null),
          canSeeSetupNotifications
            ? sendRequest(TAGS_LIST_URL, 'GET')
            : Promise.resolve(null)
        ]);

        if (!isActive) return;

        const billingProfile =
          billingProfileResult.status === 'fulfilled'
            ? billingProfileResult.value
            : null;
        const developerSuggestionResponse =
          developerSuggestionResult.status === 'fulfilled'
            ? developerSuggestionResult.value
            : null;
        const projectSuggestionResponse =
          projectSuggestionResult.status === 'fulfilled'
            ? projectSuggestionResult.value
            : null;
        const devFeedNotificationResponse =
          devFeedNotificationResult.status === 'fulfilled'
            ? devFeedNotificationResult.value
            : null;
        const projectListResponse =
          projectListResult.status === 'fulfilled'
            ? projectListResult.value
            : null;
        const tagsListResponse =
          tagsListResult.status === 'fulfilled' ? tagsListResult.value : null;
        const analysisStartDate =
          effectiveUser?.analyzeStartDate || effectiveUser?.analysisStartDate;
        const hasCompletedOnboarding =
          resolveIntegrationCompleted(effectiveUser) &&
          (resolvePlanCompleted(effectiveUser) ||
            resolvePlanCompletedFromResponse(billingProfile)) &&
          (resolvePaymentCompleted(effectiveUser) ||
            resolvePaymentCompletedFromResponse(billingProfile));

        setShowHistoricalAnalysisNotification(
          canSeeHistoricalAnalysis &&
            hasCompletedOnboarding &&
            shouldShowHistoricalAnalysisNotification(analysisStartDate)
        );
        setShowDeveloperMergeSuggestionNotification(
          canSeeDeveloperSuggestions &&
            Number(developerSuggestionResponse?.pending_count || 0) > 0
        );
        setDevFeedNotificationCount(
          canSeeDevFeed
            ? Number(devFeedNotificationResponse?.unread_count || 0)
            : 0
        );
        setShowProjectsNotification(
          canSeeSetupNotifications &&
            projectListResult.status === 'fulfilled' &&
            hasNoCustomProjects(projectListResponse?.projects)
        );
        setShowProjectSuggestionNotification(
          canSeeProjectSuggestions
            ? Number(projectSuggestionResponse?.pending_count || 0) > 0
            : false
        );
        setShowTagsNotification(
          canSeeSetupNotifications &&
            tagsListResult.status === 'fulfilled' &&
            hasNoCustomTags(tagsListResponse?.data)
        );
      } catch (error) {
        if (isActive) {
          setShowHistoricalAnalysisNotification(false);
          setShowDeveloperMergeSuggestionNotification(false);
          setShowProjectsNotification(false);
          setShowProjectSuggestionNotification(false);
          setShowTagsNotification(false);
          setDevFeedNotificationCount(0);
        }
      }
    };

    if (
      ![ROLES.ACCOUNT_ADMIN, ROLES.PANEL_ADMIN, ROLES.VIEWER].includes(userRole)
    ) {
      setShowHistoricalAnalysisNotification(false);
      setShowDeveloperMergeSuggestionNotification(false);
      setShowProjectsNotification(false);
      setShowProjectSuggestionNotification(false);
      setShowTagsNotification(false);
      setDevFeedNotificationCount(0);
      return () => {
        isActive = false;
      };
    }

    fetchNotificationState();

    return () => {
      isActive = false;
    };
  }, [effectiveUser, location.pathname, userRole]);

  // Get sidebar items based on user role
  const items = getSidebarItems(userRole, {
    notificationsByPath: {
      [DEVELOPERS_SETTINGS_PATH]: showDeveloperMergeSuggestionNotification,
      [HISTORICAL_ANALYSIS_PATH]: showHistoricalAnalysisNotification,
      [PROJECTS_SETTINGS_PATH]:
        showProjectsNotification || showProjectSuggestionNotification,
      [TAGS_SETTINGS_PATH]: showTagsNotification
    },
    notificationCountsByPath: {
      [DEV_FEED_PATH]: devFeedNotificationCount
    }
  });
  const dockSectionItems = items.filter(item =>
    BOTTOM_DOCK_SECTION_LABELS.includes(item.label)
  );
  const mainItems = items.filter(
    item => !BOTTOM_DOCK_SECTION_LABELS.includes(item.label)
  );

  const openPaths = findOpenPaths(items, location.pathname) || [];
  const dockItems = dockSectionItems;

  const [searchQuery, setSearchQuery] = useState('');

  // Filter items based on search
  const filterItems = items => {
    if (!searchQuery.trim()) return items;

    return items
      .map(item => {
        const matchesSearch = item.label
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
        const hasMatchingChildren =
          item.nodes?.some(child =>
            child.label.toLowerCase().includes(searchQuery.toLowerCase())
          ) || false;

        if (matchesSearch || hasMatchingChildren) {
          return {
            ...item,
            nodes: item.nodes?.filter(child =>
              child.label.toLowerCase().includes(searchQuery.toLowerCase())
            )
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  const filteredMainItems = filterItems(mainItems);
  const filteredDockItems = filterItems(dockItems);

  // Collapsed mode - show icons with popups
  if (isCollapsed) {
    const allItems = [...mainItems, ...dockItems];
    return (
      <div className="flex h-full flex-col px-2 py-4">
        <div className="flex flex-col gap-1">
          {allItems.map((item, idx) => {
            const IconComponent =
              item.icon && Icons[item.icon] ? Icons[item.icon] : null;
            const isActive = item.path === location.pathname;
            const isActiveParent = openPaths.includes(item.label);
            const notificationCount = getItemNotificationCount(item);
            const hasNotification = hasItemNotification(item);
            const hasChildren = item.nodes && item.nodes.length > 0;

            return (
              <div key={idx} className="group relative">
                <button
                  onClick={() => {
                    if (item.path) {
                      handleLinkClick(item.path, navigate);
                    }
                  }}
                  className={`flex h-12 w-12 items-center justify-center rounded-lg transition-all ${
                    isActive || isActiveParent
                      ? 'bg-primary text-white shadow-lg'
                      : 'text-bodydark2 hover:bg-white/10 hover:text-white'
                  }`}
                  title={item.label}
                >
                  {IconComponent && <IconComponent fontSize="small" />}
                  {notificationCount > 0 && (
                    <span className="absolute right-0 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 text-[9px] font-bold text-white">
                      {notificationCount > 9 ? '9+' : notificationCount}
                    </span>
                  )}
                  {!notificationCount && hasNotification && (
                    <span className="absolute right-1 top-1 flex h-2.5 w-2.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-500 shadow-lg shadow-rose-500/50" />
                    </span>
                  )}
                </button>

                {/* Submenu Popup */}
                {hasChildren && (
                  <div className="pointer-events-none absolute left-full top-0 z-50 ml-2 min-w-[200px] rounded-lg border border-white/10 bg-[#1a1a1a] p-2 opacity-0 shadow-2xl transition-all group-hover:pointer-events-auto group-hover:opacity-100">
                    <div className="mb-2 border-b border-white/10 px-2 pb-2 text-xs font-semibold uppercase tracking-wider text-bodydark2">
                      {item.label}
                    </div>
                    <div className="space-y-0.5">
                      {item.nodes.map((child, childIdx) => {
                        const isChildActive = child.path === location.pathname;
                        const childNotificationCount =
                          getItemNotificationCount(child);
                        const childHasNotification = hasItemNotification(child);
                        return (
                          <button
                            key={childIdx}
                            onClick={() =>
                              handleLinkClick(child.path, navigate)
                            }
                            className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-all ${
                              isChildActive
                                ? 'bg-primary/20 text-white'
                                : 'text-bodydark2 hover:bg-white/10 hover:text-white'
                            }`}
                          >
                            <span className="flex-1 truncate">
                              {child.label}
                            </span>
                            {child.badge && (
                              <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-xs font-medium text-amber-400">
                                {child.badge}
                              </span>
                            )}
                            {childNotificationCount > 0 && (
                              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white">
                                {childNotificationCount > 9
                                  ? '9+'
                                  : childNotificationCount}
                              </span>
                            )}
                            {!childNotificationCount &&
                              childHasNotification && (
                                <span className="relative flex h-2 w-2">
                                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
                                  <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-500 shadow-lg shadow-rose-500/50" />
                                </span>
                              )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Tooltip for items without children */}
                {!hasChildren && (
                  <div className="pointer-events-none absolute left-full top-1/2 ml-2 -translate-y-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-2 text-sm text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                    {item.label}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Search Bar */}
      <div className="px-3 pb-2 pt-3">
        <div className="relative">
          <Icons.Search
            fontSize="small"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-bodydark2"
          />
          <input
            type="text"
            placeholder="Search menu..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-white/5 bg-white/5 py-2 pl-10 pr-3 text-sm text-white placeholder-bodydark2 transition-all focus:border-primary/50 focus:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-bodydark2 transition-colors hover:text-white"
            >
              <Icons.Close fontSize="small" />
            </button>
          )}
        </div>
      </div>

      {/* Main Navigation */}
      <div className="flex-1 overflow-y-auto px-3 pb-3">
        {/* Workspace Section */}
        {filteredMainItems.length > 0 && (
          <div className="mb-5">
            <div className="mb-2 flex items-center gap-2 px-2">
              <div className="h-1 w-1 rounded-full bg-primary/60" />
              <span className="text-xs font-bold uppercase tracking-widest text-bodydark2/80">
                Workspace
              </span>
              <div className="flex-1 border-b border-white/5" />
            </div>
            <div className="space-y-0.5">
              {filteredMainItems.map((item, idx) => (
                <MenuItem
                  key={idx}
                  item={item}
                  initiallyOpenPaths={openPaths}
                  searchQuery={searchQuery}
                />
              ))}
            </div>
          </div>
        )}

        {/* Settings Section */}
        {filteredDockItems.length > 0 && (
          <div>
            <div className="mb-2 flex items-center gap-2 px-2">
              <div className="h-1 w-1 rounded-full bg-bodydark2/60" />
              <span className="text-xs font-bold uppercase tracking-widest text-bodydark2/80">
                Settings
              </span>
              <div className="flex-1 border-b border-white/5" />
            </div>
            <div className="space-y-0.5">
              {filteredDockItems.map((item, idx) => (
                <MenuItem
                  key={`dock-${idx}`}
                  item={item}
                  initiallyOpenPaths={openPaths}
                  searchQuery={searchQuery}
                />
              ))}
            </div>
          </div>
        )}

        {/* No results message */}
        {searchQuery &&
          filteredMainItems.length === 0 &&
          filteredDockItems.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Icons.SearchOff
                className="mb-2 text-bodydark2"
                fontSize="large"
              />
              <p className="text-sm text-bodydark2">No menu items found</p>
              <p className="mt-1 text-xs text-bodydark2/60">
                Try a different search term
              </p>
            </div>
          )}
      </div>
    </div>
  );
};

export default SidebarContent;
